/**
 * Date/time presentation for the demo-booking calendar (GH-897/898, now GH-901: real slots from
 * `/v1/public/demo-availability` instead of client-invented weekday math). Every format here is
 * explicitly pinned to `America/New_York` ("Eastern Time", the label already shown beside the
 * calendar) rather than the visitor's own browser timezone — without that, a slot grouped by the
 * visitor's local calendar date could land on the wrong day for anyone west or east of Eastern.
 */
import type { OpenSlot } from "../api/demo-booking.js";

const EASTERN_TZ = "America/New_York";
const WEEKDAY_SHORT = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: EASTERN_TZ });
const DAY_NUMBER = new Intl.DateTimeFormat("en-US", { day: "numeric", timeZone: EASTERN_TZ });
const MONTH_LONG = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: EASTERN_TZ });
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: EASTERN_TZ,
});
const FULL_DATE = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  timeZone: EASTERN_TZ,
});
const TIME_LABEL = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZone: EASTERN_TZ,
});
/** "en-CA" formats as YYYY-MM-DD — a stable, sortable grouping key, not shown to anyone. */
const DATE_KEY = new Intl.DateTimeFormat("en-CA", { timeZone: EASTERN_TZ });

export interface DateGroup {
  readonly dateKey: string;
  readonly date: Date;
  readonly slots: readonly OpenSlot[];
}

/** Open slots, grouped into calendar dates (Eastern Time) and sorted earliest first, both
 *  the dates and each date's own slots. */
export function groupSlotsByDate(slots: readonly OpenSlot[]): DateGroup[] {
  const byDate = new Map<string, OpenSlot[]>();
  for (const slot of slots) {
    const key = DATE_KEY.format(slot.startsAt);
    const existing = byDate.get(key);
    if (existing) existing.push(slot);
    else byDate.set(key, [slot]);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dateKey, daySlots]) => {
      const sorted = [...daySlots].sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
      return { dateKey, date: sorted[0]!.startsAt, slots: sorted };
    });
}

export function dateTileLabel(date: Date): { weekday: string; day: number } {
  return { weekday: WEEKDAY_SHORT.format(date), day: Number(DAY_NUMBER.format(date)) };
}

/** A single "Month Year" heading when every date shares one, otherwise a spanning range. */
export function monthHeadingFor(dates: readonly Date[]): string {
  if (dates.length === 0) return "";
  const first = dates[0]!;
  const last = dates[dates.length - 1]!;
  if (DATE_KEY.format(first).slice(0, 7) === DATE_KEY.format(last).slice(0, 7)) {
    return MONTH_YEAR.format(first);
  }
  return `${MONTH_LONG.format(first)} – ${MONTH_YEAR.format(last)}`;
}

export function timeLabel(slot: OpenSlot): string {
  return TIME_LABEL.format(slot.startsAt);
}

export function formatBookingSelection(slot: OpenSlot): string {
  return `${FULL_DATE.format(slot.startsAt)} at ${TIME_LABEL.format(slot.startsAt)} (Eastern)`;
}
